import { fileViewed } from 'features/content-library/actions';
import {
  embedPlayerStateApplied,
  fileOpened,
  loaderDisplayChanged,
  selectActiveFile,
  selectContentLibraryMediaPlayer,
} from 'features/content-library/contentLibrarySlice';
import {
  ContentLibraryOpenedFile,
  ContentLibraryMediaPlayer,
  ContentLibraryMediaPlayerRemotePayload,
} from 'features/content-library/types';
import { call, put, select } from 'redux-saga/effects';
import { isContentLibraryMediaPlayer } from 'features/content-library/utils/isContentLibraryMediaPlayer';
import { requestMediaPlayerState } from 'features/content-library/utils/requestMediaPlayerState';
import { requestContentWhiteboardData } from 'features/content-whiteboard/actions';
import { CONTENT_BOARD_SUPPORTED_FILE_TYPES } from 'features/content-whiteboard/constants';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { logger } from 'utils/logger';

export function* onContentLibraryFileViewedSaga(action: ReturnType<typeof fileViewed>) {
  const { id, data } = action.payload;
  const file: ContentLibraryOpenedFile | undefined = yield select(selectActiveFile, id);

  if (file) {
    let serverPlayerState: ContentLibraryMediaPlayerRemotePayload | undefined;

    // Fetch server state BEFORE opening the file — YouTube's playerVars.start is only applied at mount time.
    const isMediaPlayerType: boolean = yield call(isContentLibraryMediaPlayer, file);
    if (isMediaPlayerType) {
      serverPlayerState = yield call(requestMediaPlayerState, file.id);
      if (file.type === 'audio') {
        logger
          .remote({ capture: 'mp3-debug', nonProd: true })
          .log('[fileViewedSaga] requestMediaPlayerState', {
            id: file.id,
            fileType: file.type,
            serverPlayerState,
          });
      }
    }

    const currentPage = data?.page ?? file.page ?? 0;

    yield put(loaderDisplayChanged(true));
    yield call(contentBoard.clean);

    if (file.type === 'audio') {
      logger
        .remote({ capture: 'mp3-debug', nonProd: true })
        .log('[fileViewedSaga] dispatching fileOpened', {
          id: file.id,
          url: data?.url ?? file.url,
        });
    }

    yield put(
      fileOpened({
        ...file,
        page: currentPage,
        url: data?.url ?? file.url,
      } as ContentLibraryOpenedFile)
    );

    // Only apply if no live event updated the player while we were fetching
    // (prevents a stale snapshot from overwriting a more recent state change).
    if (serverPlayerState) {
      const currentPlayerState: ContentLibraryMediaPlayer | undefined = yield select(
        selectContentLibraryMediaPlayer,
        file.id
      );

      if (file.type === 'audio') {
        logger
          .remote({ capture: 'mp3-debug', nonProd: true })
          .log('[fileViewedSaga] applying server state?', {
            id: file.id,
            currentReduxState: currentPlayerState?.state,
            willApply: currentPlayerState?.state === 'idle',
          });
      }

      if (currentPlayerState?.state === 'idle') {
        yield put(embedPlayerStateApplied(serverPlayerState, { source: 'snapshot' }));
      }
    }

    if (CONTENT_BOARD_SUPPORTED_FILE_TYPES.includes(file.type)) {
      yield put(requestContentWhiteboardData({ id: file.id, page: currentPage }));
    }
  }
}
